"use client";

import React from "react";
import Link from "next/link";
import PageBreadCrumb from "@/components/common/PageBreadCrumb";
import DataTable from "@/components/ui/DataTable";
import DatePicker from "@/components/form/date-picker";
import Pagination from "@/components/tables/Pagination";
import Button from "@/components/ui/button/Button";
import { rmcClient } from "@/lib/rmcClient";
import { useQuery } from "@tanstack/react-query";

const PAGE_SIZE = 20;

function pickCtx(ctx, ...keys) {
  if (!ctx || typeof ctx !== "object") return "";
  for (const k of keys) {
    const v = ctx[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return String(v);
  }
  return "";
}

export default function RmcFuelReadings() {
  const [filters, setFilters] = React.useState({
    date_from: "",
    date_to: "",
  });
  const [page, setPage] = React.useState(1);

  const queryParams = React.useMemo(
    () => ({
      ...(filters.date_from ? { date_from: filters.date_from } : {}),
      ...(filters.date_to ? { date_to: filters.date_to } : {}),
      page,
      page_size: PAGE_SIZE,
    }),
    [filters, page]
  );

  const { data: listData, isFetching } = useQuery({
    queryKey: ["fuelReadings", queryParams],
    queryFn: async () => (await rmcClient.listFuelReadings(queryParams)).data,
    keepPreviousData: true,
  });

  const rows = React.useMemo(() => {
    if (Array.isArray(listData)) return listData;
    if (Array.isArray(listData?.results)) return listData.results;
    if (Array.isArray(listData?.data?.results)) return listData.data.results;
    return [];
  }, [listData]);

  const totalCount = React.useMemo(() => {
    const v = Number(listData?.count);
    if (Number.isFinite(v) && v >= 0) return v;
    const n = Number(listData?.data?.count);
    if (Number.isFinite(n) && n >= 0) return n;
    return rows.length;
  }, [listData, rows.length]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);

  const updateFilters = React.useCallback((next) => {
    setFilters((prev) => ({ ...prev, ...next }));
    setPage(1);
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <PageBreadCrumb pageTitle="Fuel readings" />
        <Link href="/transport/fuel-readings/new">
          <Button size="sm" type="button">
            New entry
          </Button>
        </Link>
      </div>

      <div className="rounded-2xl border border-gray-200/80 bg-white/90 p-4 dark:border-gray-800 dark:bg-gray-900/50">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Date from</label>
            <DatePicker
              id="fuel-filter-from"
              defaultDate={filters.date_from}
              placeholder="Any"
              onChange={(_, dateStr) => updateFilters({ date_from: dateStr || "" })}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Date to</label>
            <DatePicker
              id="fuel-filter-to"
              defaultDate={filters.date_to}
              placeholder="Any"
              onChange={(_, dateStr) => updateFilters({ date_to: dateStr || "" })}
            />
          </div>
        </div>
      </div>

      <DataTable
        columns={[
          {
            key: "date",
            header: "Date",
            render: (r) => r.date || "—",
          },
          {
            key: "voucher",
            header: "Voucher no.",
            render: (r) =>
              pickCtx(r, "voucher_no", "voucher_number", "number") || (r.id != null ? `#${r.id}` : "—"),
          },
          { key: "book_name", header: "Book", render: (r) => r.book_name || "—" },
          { key: "fuel_type", header: "Fuel type", render: (r) => r.fuel_type || "—" },
          {
            key: "lines",
            header: "Lines",
            render: (r) => {
              const n = Array.isArray(r.items)
                ? r.items.length
                : Array.isArray(r.lines)
                  ? r.lines.length
                  : null;
              return n != null ? n : "—";
            },
          },
          {
            key: "actions",
            header: "",
            headerClassName: "text-right",
            cellClassName: "text-right",
            render: (r) => {
              const id = r?.id ?? r?.pk;
              if (!id) return null;
              return (
                <Link href={`/transport/fuel-readings/${id}/edit`}>
                  <Button type="button" size="sm" variant="outline">
                    Open
                  </Button>
                </Link>
              );
            },
          },
        ]}
        data={rows}
        isLoading={isFetching}
      />

      <div className="flex justify-end">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(p) => setPage(Math.max(1, Math.min(totalPages, p)))}
        />
      </div>
    </div>
  );
}
